import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import GetAppIcon from "@material-ui/icons/GetApp";
import FilterListIcon from "@material-ui/icons/FilterList";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";
import {
  getInvoiceStatusLabel,
  INVOICE_STATUS_LABELS,
  formatPaymentMethod
} from "../../constants/invoiceStatus";

const useStyles = makeStyles((theme) => ({
  widePopover: {
    maxWidth: "none !important",
    minWidth: "340px !important",
  },
  kpiCard: {
    padding: theme.spacing(2),
    textAlign: "center",
  },
  kpiValue: {
    fontSize: "1.6rem",
    fontWeight: 700,
  },
  kpiSub: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  kpiLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: 4,
  },
  filterCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    marginBottom: theme.spacing(2),
  },
  filterCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    backgroundColor: `${theme.palette.primary.main}0F`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  filterCardTitle: {
    fontSize: "0.85rem",
    fontWeight: 700,
  },
  filterCount: {
    height: 20,
    fontSize: "0.7rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}24`,
  },
  clearFiltersButton: {
    fontSize: "0.75rem",
  },
  exportButton: {
    marginLeft: "auto",
    fontSize: "0.75rem",
  },
  filterCardBody: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  filterGroupLabel: {
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "flex-end",
  },
  rangeBlock: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1, 0.5),
  },
  rangeBlockLabel: {
    display: "block",
    fontSize: "0.6rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  rangePair: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(0.5),
  },
  rangeDash: {
    color: theme.palette.text.secondary,
    paddingBottom: theme.spacing(1.25),
  },
  searchField: { minWidth: 240 },
  filterControl: { minWidth: 180 },
  dateField: { width: 160 },
  valorField: { width: 120 },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
}));

const PAGE_LIMIT = 25;

const ACTIVATION_OPTIONS = [
  { value: "active", label: "Ativos" },
  { value: "inactive", label: "Inativos" },
  { value: "trial", label: "Trial" },
];

const COMPANY_STATUS_OPTIONS = [
  { value: "adimplente", label: "Adimplente" },
  { value: "inadimplente", label: "Inadimplente" },
];

const PAYMENT_STATUS_OPTIONS = Object.keys(INVOICE_STATUS_LABELS)
  .filter((key) => key !== "canceled")
  .map((key) => ({ value: key, label: INVOICE_STATUS_LABELS[key].label }));

const FORMA_OPTIONS = [
  { value: "CREDIT_CARD", label: "Cartão de Crédito" },
  { value: "PIX", label: "PIX" },
  { value: "BOLETO", label: "Boleto" },
];

const KPI_CARDS = [
  { key: "recebido", label: "Recebido", color: "#2e7d32" },
  { key: "pendente", label: "Pendente", color: "#9e6b00" },
  { key: "vencido", label: "Vencido", color: "#c62828" },
  { key: "reembolsado", label: "Reembolsado", color: "#ad1457" },
];

const formatBRL = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `R$ ${n.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
};

const formatDate = (d) => (d ? moment(d).format("DD/MM/YYYY") : "—");

const ActivationChip = ({ active, trial }) => {
  if (trial) {
    return (
      <Chip size="small" label="Trial" style={{ backgroundColor: "#fff3e0", color: "#e65100" }} />
    );
  }
  return active ? (
    <Chip size="small" label="Ativo" style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }} />
  ) : (
    <Chip size="small" label="Inativo" style={{ backgroundColor: "#ffebee", color: "#c62828" }} />
  );
};

const CompanyStatusChip = ({ status }) =>
  status === "inadimplente" ? (
    <Chip size="small" label="Inadimplente" style={{ backgroundColor: "#ffebee", color: "#c62828" }} />
  ) : (
    <Chip size="small" label="Adimplente" style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }} />
  );

const PaymentStatusChip = ({ status }) => {
  const m = getInvoiceStatusLabel(status);
  return <Chip size="small" label={m.label} style={{ backgroundColor: m.bg, color: m.fg }} />;
};

const PaymentView = () => {
  const classes = useStyles();
  const { list: listPlans } = usePlans();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [kpis, setKpis] = useState({
    recebido: { count: 0, total: 0 },
    pendente: { count: 0, total: 0 },
    vencido: { count: 0, total: 0 },
    reembolsado: { count: 0, total: 0 },
  });
  const [pageNumber, setPageNumber] = useState(1);

  const [dueDateStart, setDueDateStart] = useState("");
  const [dueDateEnd, setDueDateEnd] = useState("");
  const [paidDateStart, setPaidDateStart] = useState("");
  const [paidDateEnd, setPaidDateEnd] = useState("");
  const [activationFilter, setActivationFilter] = useState([]);
  const [companyStatusFilter, setCompanyStatusFilter] = useState([]);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState([]);
  const [planFilter, setPlanFilter] = useState([]);
  const [formaFilter, setFormaFilter] = useState([]);
  const [valorMin, setValorMin] = useState("");
  const [valorMax, setValorMax] = useState("");
  const [planOptions, setPlanOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [suggestionOptions, setSuggestionOptions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Fetch autocomplete suggestions as the user types (debounced 300ms) —
  // same pattern as Visão por Empresa's search field.
  useEffect(() => {
    if (selectedCompany) return undefined;
    const term = searchInput.trim();
    if (!term) {
      setSuggestionOptions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      setSuggestionsLoading(true);
      try {
        const { data } = await api.get("/companies/super", {
          params: { searchParam: term, limit: 10 }
        });
        setSuggestionOptions(data.rows || []);
      } catch (err) {
        // silent — suggestion fetch failures shouldn't surface as toasts
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, selectedCompany]);

  // Load plan dropdown options
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const plans = await listPlans();
        if (active) setPlanOptions(plans || []);
      } catch (err) {
        if (active) toastError(err);
      }
    })();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageNumber(1);
  }, [dueDateStart, dueDateEnd, paidDateStart, paidDateEnd, activationFilter, companyStatusFilter, paymentStatusFilter, planFilter, formaFilter, valorMin, valorMax, selectedCompany]);

  const buildParams = useCallback(() => ({
    dueDateStart: dueDateStart || undefined,
    dueDateEnd: dueDateEnd || undefined,
    paidDateStart: paidDateStart || undefined,
    paidDateEnd: paidDateEnd || undefined,
    activationStatus: activationFilter.length > 0 ? activationFilter.join(",") : undefined,
    companyStatus: companyStatusFilter.length > 0 ? companyStatusFilter.join(",") : undefined,
    paymentStatus: paymentStatusFilter.length > 0 ? paymentStatusFilter.join(",") : undefined,
    planId: planFilter.length > 0 ? planFilter.join(",") : undefined,
    paymentFlow: formaFilter.length > 0 ? formaFilter.join(",") : undefined,
    valorMin: valorMin || undefined,
    valorMax: valorMax || undefined,
    companyId: selectedCompany?.id || undefined,
  }), [dueDateStart, dueDateEnd, paidDateStart, paidDateEnd, activationFilter, companyStatusFilter, paymentStatusFilter, planFilter, formaFilter, valorMin, valorMax, selectedCompany]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices/super", {
        params: { ...buildParams(), pageNumber, limit: PAGE_LIMIT },
      });
      setRows(data.rows || []);
      setCount(data.count || 0);
      setHasMore(!!data.hasMore);
      setKpis(
        data.kpis || {
          recebido: { count: 0, total: 0 },
          pendente: { count: 0, total: 0 },
          vencido: { count: 0, total: 0 },
          reembolsado: { count: 0, total: 0 },
        }
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [buildParams, pageNumber]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const filterParams = buildParams();
      let allRows = [];
      let page = 1;
      let more = true;
      while (more) {
        const { data } = await api.get("/invoices/super", {
          params: { ...filterParams, pageNumber: page, limit: 100 },
        });
        allRows = allRows.concat(data.rows || []);
        more = !!data.hasMore;
        page += 1;
      }

      const headers = [
        "Empresa",
        "Plano",
        "Vencimento",
        "Status de Ativação",
        "Forma",
        "Pagamento",
        "Status de Empresa",
        "Status de Pagamento",
        "Alterado Manualmente",
        "Valor",
      ];
      // Date fields are wrapped as ="DD/MM/YYYY" so Excel treats them as
      // literal text instead of silently retyping them as Date cells and
      // showing ####### when the column is narrower than the formatted value.
      const csvRows = allRows.map((row) => [
        row.companyName || "",
        row.planName || "",
        row.dueDate ? `="${moment(row.dueDate).format("DD/MM/YYYY")}"` : "",
        row.planTrial ? "Trial" : row.companyActive ? "Ativo" : "Inativo",
        formatPaymentMethod(row.paymentFlow, row.paymentMethod),
        row.status === "paid" && (row.paidAt || row.manualStatusChangeAt)
          ? `="${moment(row.paidAt || row.manualStatusChangeAt).format("DD/MM/YYYY")}"`
          : "",
        row.companyStatus === "inadimplente" ? "Inadimplente" : "Adimplente",
        getInvoiceStatusLabel(row.status).label,
        row.manualStatusChangeAt
          ? `Sim - ${moment(row.manualStatusChangeAt).format("DD/MM/YYYY")}`
          : "Não",
        Number(row.value || 0).toFixed(2).replace(".", ","),
      ]);
      const csv = [headers, ...csvRows]
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"))
        .join("\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `financeiro-pagamentos-${moment().format("YYYY-MM-DD")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_LIMIT));

  const activeFilterCount = [
    Boolean(dueDateStart) || Boolean(dueDateEnd),
    Boolean(paidDateStart) || Boolean(paidDateEnd),
    activationFilter.length > 0,
    companyStatusFilter.length > 0,
    paymentStatusFilter.length > 0,
    planFilter.length > 0,
    formaFilter.length > 0,
    Boolean(valorMin),
    Boolean(valorMax),
    Boolean(selectedCompany),
  ].filter(Boolean).length;

  return (
    <>
      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 6, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {dueDateStart || dueDateEnd || paidDateStart || paidDateEnd
          ? "Totais do período filtrado"
          : "Totais gerais (todos os pagamentos)"}
      </Typography>
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {KPI_CARDS.map(({ key, label, color }) => (
          <Grid item xs={12} sm={3} key={key}>
            <Paper variant="outlined" className={classes.kpiCard}>
              <div className={classes.kpiValue} style={{ color }}>
                {formatBRL(kpis[key].total)}
              </div>
              <div className={classes.kpiSub}>{kpis[key].count} pagamento(s)</div>
              <div className={classes.kpiLabel}>{label}</div>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <div className={classes.filterCard}>
        <div className={classes.filterCardHeader}>
          <FilterListIcon color="primary" fontSize="small" />
          <Typography className={classes.filterCardTitle}>Filtros</Typography>
          {activeFilterCount > 0 && (
            <Chip
              size="small"
              className={classes.filterCount}
              label={`${activeFilterCount} ativo${activeFilterCount > 1 ? "s" : ""}`}
            />
          )}
          {activeFilterCount > 0 && (
            <Button
              size="small"
              variant="text"
              color="secondary"
              className={classes.clearFiltersButton}
              onClick={() => {
                setDueDateStart("");
                setDueDateEnd("");
                setPaidDateStart("");
                setPaidDateEnd("");
                setActivationFilter([]);
                setCompanyStatusFilter([]);
                setPaymentStatusFilter([]);
                setPlanFilter([]);
                setFormaFilter([]);
                setValorMin("");
                setValorMax("");
                setSelectedCompany(null);
                setSearchInput("");
              }}
            >
              Limpar filtros
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            className={classes.exportButton}
            startIcon={exporting ? <CircularProgress size={16} /> : <GetAppIcon />}
            disabled={exporting}
            onClick={exportCsv}
          >
            Exportar CSV
          </Button>
        </div>
        <div className={classes.filterCardBody}>
          <div>
            <div className={classes.filterGroupLabel}>Empresa</div>
            <div className={classes.filterRow}>
              <Autocomplete
                size="small"
                className={classes.searchField}
                value={selectedCompany}
                onChange={(_event, newValue) => {
                  setSelectedCompany(newValue);
                  if (!newValue) setSearchInput("");
                }}
                inputValue={searchInput}
                onInputChange={(_event, newInput, reason) => {
                  if (reason === "reset") return;
                  setSearchInput(newInput);
                }}
                options={suggestionOptions}
                loading={suggestionsLoading}
                getOptionLabel={(option) => option?.name || ""}
                getOptionSelected={(option, val) => option.id === val?.id}
                filterOptions={(opts) => opts}
                noOptionsText="Nenhuma empresa encontrada"
                loadingText="Buscando..."
                renderOption={(option) => (
                  <div>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="textSecondary">
                        {option.email}
                      </Typography>
                    )}
                  </div>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Buscar (nome ou e-mail)"
                  />
                )}
              />
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel shrink>Plano</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  label="Plano"
                  notched
                  MenuProps={{ classes: { paper: classes.widePopover } }}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: "#9e9e9e" }}>Todos</span>
                      : selected.map(v => planOptions.find(p => String(p.id) === String(v))?.name || v).join(", ")
                  }
                >
                  {planOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Checkbox checked={planFilter.some(v => String(v) === String(p.id))} />
                      <ListItemText primary={p.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div>
            <div className={classes.filterGroupLabel}>Período</div>
            <div className={classes.filterRow}>
              <div className={classes.rangeBlock}>
                <span className={classes.rangeBlockLabel}>Vencimento</span>
                <div className={classes.rangePair}>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="date"
                    label="Início"
                    className={classes.dateField}
                    InputLabelProps={{ shrink: true }}
                    value={dueDateStart}
                    onChange={(e) => setDueDateStart(e.target.value)}
                  />
                  <span className={classes.rangeDash}>—</span>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="date"
                    label="Fim"
                    className={classes.dateField}
                    InputLabelProps={{ shrink: true }}
                    value={dueDateEnd}
                    onChange={(e) => setDueDateEnd(e.target.value)}
                  />
                </div>
              </div>
              <div className={classes.rangeBlock}>
                <span className={classes.rangeBlockLabel}>Pagamento</span>
                <div className={classes.rangePair}>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="date"
                    label="Início"
                    className={classes.dateField}
                    InputLabelProps={{ shrink: true }}
                    value={paidDateStart}
                    onChange={(e) => setPaidDateStart(e.target.value)}
                  />
                  <span className={classes.rangeDash}>—</span>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="date"
                    label="Fim"
                    className={classes.dateField}
                    InputLabelProps={{ shrink: true }}
                    value={paidDateEnd}
                    onChange={(e) => setPaidDateEnd(e.target.value)}
                  />
                </div>
              </div>
              <div className={classes.rangeBlock}>
                <span className={classes.rangeBlockLabel}>Valor</span>
                <div className={classes.rangePair}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Mín."
                    className={classes.valorField}
                    value={valorMin}
                    onChange={(e) => setValorMin(e.target.value.replace(/[^0-9.,]/g, ""))}
                    inputProps={{ inputMode: "decimal" }}
                  />
                  <span className={classes.rangeDash}>—</span>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Máx."
                    className={classes.valorField}
                    value={valorMax}
                    onChange={(e) => setValorMax(e.target.value.replace(/[^0-9.,]/g, ""))}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className={classes.filterGroupLabel}>Status</div>
            <div className={classes.filterRow}>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel shrink>Status de Ativação</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={activationFilter}
                  onChange={(e) => setActivationFilter(e.target.value)}
                  label="Status de Ativação"
                  notched
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: "#9e9e9e" }}>Todos</span>
                      : selected.map(v => ACTIVATION_OPTIONS.find(o => o.value === v)?.label || v).join(", ")
                  }
                >
                  {ACTIVATION_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={activationFilter.includes(o.value)} />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel shrink>Status da Empresa</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={companyStatusFilter}
                  onChange={(e) => setCompanyStatusFilter(e.target.value)}
                  label="Status da Empresa"
                  notched
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: "#9e9e9e" }}>Todos</span>
                      : selected.map(v => COMPANY_STATUS_OPTIONS.find(o => o.value === v)?.label || v).join(", ")
                  }
                >
                  {COMPANY_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={companyStatusFilter.includes(o.value)} />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel shrink>Status de Pagamento</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  label="Status de Pagamento"
                  notched
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: "#9e9e9e" }}>Todos</span>
                      : selected.map(v => PAYMENT_STATUS_OPTIONS.find(o => o.value === v)?.label || v).join(", ")
                  }
                >
                  {PAYMENT_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={paymentStatusFilter.includes(o.value)} />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel shrink>Forma</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={formaFilter}
                  onChange={(e) => setFormaFilter(e.target.value)}
                  label="Forma"
                  notched
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: "#9e9e9e" }}>Todas</span>
                      : selected.map(v => FORMA_OPTIONS.find(o => o.value === v)?.label || v).join(", ")
                  }
                >
                  {FORMA_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={formaFilter.includes(o.value)} />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        </div>
      </div>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Empresa</TableCell>
            <TableCell>Plano</TableCell>
            <TableCell>Vencimento</TableCell>
            <TableCell>Status de Ativação</TableCell>
            <TableCell>Forma</TableCell>
            <TableCell>Pagamento</TableCell>
            <TableCell>Status de Empresa</TableCell>
            <TableCell>Status de Pagamento</TableCell>
            <TableCell>Alterado Manualmente</TableCell>
            <TableCell align="right">Valor</TableCell>
          </TableRow>
        </TableHead>
        <TableBody style={{ opacity: loading && rows.length > 0 ? 0.5 : 1, transition: "opacity 0.15s ease" }}>
          {loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Box py={4}>
                  <CircularProgress size={28} />
                </Box>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Box py={4}>
                  <Typography color="textSecondary">
                    Nenhum pagamento encontrado para os filtros selecionados.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.companyName}</TableCell>
                <TableCell>{row.planName || "—"}</TableCell>
                <TableCell>{formatDate(row.dueDate)}</TableCell>
                <TableCell>
                  <ActivationChip active={row.companyActive} trial={row.planTrial} />
                </TableCell>
                <TableCell>{formatPaymentMethod(row.paymentFlow, row.paymentMethod)}</TableCell>
                <TableCell>
                  {row.status === "paid" ? formatDate(row.paidAt || row.manualStatusChangeAt) : "—"}
                </TableCell>
                <TableCell>
                  <CompanyStatusChip status={row.companyStatus} />
                </TableCell>
                <TableCell>
                  <PaymentStatusChip status={row.status} />
                </TableCell>
                <TableCell>
                  {row.manualStatusChangeAt ? `Sim - ${formatDate(row.manualStatusChangeAt)}` : "Não"}
                </TableCell>
                <TableCell align="right">{formatBRL(row.value)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div className={classes.pagination}>
        <Typography variant="body2" color="textSecondary">
          {count > 0
            ? `Mostrando ${(pageNumber - 1) * PAGE_LIMIT + 1}–${
                (pageNumber - 1) * PAGE_LIMIT + rows.length
              } de ${count}`
            : ""}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={pageNumber <= 1 || loading}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <Typography variant="body2">
            Página {pageNumber} de {totalPages}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={!hasMore || loading}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Próxima
          </Button>
        </Box>
      </div>
    </>
  );
};

export default PaymentView;
